import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Phone,
  Mail,
  MessageCircle,
  FileText,
  Calendar,
  Hammer,
  CheckCircle,
  ArrowDown,
} from "lucide-react";

const stepIcons = [MessageCircle, FileText, Calendar, Hammer, CheckCircle];

export default function Arbeitsweise() {
  const { t } = useTranslation();

  const steps = [1, 2, 3, 4, 5].map((num, index) => ({
    number: String(num).padStart(2, '0'),
    title: t(`workProcess.steps.step${num}.title`),
    description: t(`workProcess.steps.step${num}.description`),
    icon: stepIcons[index],
  }));

  const qualityPromises = [
    {
      title: t('workProcess.qualityPromise.cleanSite.title'),
      description: t('workProcess.qualityPromise.cleanSite.description'),
    },
    {
      title: t('workProcess.qualityPromise.punctuality.title'),
      description: t('workProcess.qualityPromise.punctuality.description'),
    },
    {
      title: t('workProcess.qualityPromise.transparentPrices.title'),
      description: t('workProcess.qualityPromise.transparentPrices.description'),
    },
    {
      title: t('workProcess.qualityPromise.respect.title'),
      description: t('workProcess.qualityPromise.respect.description'),
    },
  ];

  return (
    <div data-testid="arbeitsweise-page">
      {/* Hero Section */}
      <section className="bg-[#2D3748] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold font-['Manrope'] text-white mb-6">
              {t('workProcess.hero.title')}
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              {t('workProcess.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16 md:py-24 bg-[#F7FAFC]" data-testid="process-steps">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-[#2D3748] mb-4">
              {t('workProcess.steps.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('workProcess.steps.subtitle')}
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-6 md:left-8 top-0 bottom-0 w-0.5 bg-orange-200 hidden md:block" />

            <div className="space-y-8">
              {steps.map((step, index) => (
                <div key={index} className="relative" data-testid={`process-step-${index}`}>
                  <Card className="bg-white border hover:shadow-lg transition-all duration-300 md:ml-16">
                    <CardContent className="p-6 md:p-8">
                      <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                        <div className="md:hidden flex items-center gap-3 mb-2">
                          <span className="text-3xl font-bold text-orange-500 font-['Manrope']">
                            {step.number}
                          </span>
                          <div className="h-0.5 flex-1 bg-orange-200" />
                        </div>

                        <div className="p-3 rounded-xl bg-orange-100 text-orange-500 h-fit flex-shrink-0">
                          <step.icon className="h-6 w-6" />
                        </div>

                        <div className="flex-1">
                          <h3 className="text-xl md:text-2xl font-semibold font-['Manrope'] text-[#2D3748] mb-3">
                            {step.title}
                          </h3>
                          <p className="text-gray-600 leading-relaxed">{step.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="absolute left-0 top-8 hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-orange-500 text-white font-bold font-['Manrope'] text-lg shadow-lg">
                    {step.number}
                  </div>

                  {index < steps.length - 1 && (
                    <div className="flex justify-center md:hidden my-4">
                      <ArrowDown className="h-6 w-6 text-orange-400" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quality Promise */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-[#2D3748] mb-6">
                {t('workProcess.qualityPromise.title')}
              </h2>
              <div className="space-y-4">
                {qualityPromises.map((promise, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-orange-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-[#2D3748]">{promise.title}</h4>
                      <p className="text-gray-600">{promise.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1617048530929-0edab8608369?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxjYXJwZW50ZXIlMjB0b29scyUyMG9uJTIwdGFibGUlMjB3b29kfGVufDB8fHx8MTc2OTc1OTc1M3ww&ixlib=rb-4.1.0&q=85"
                alt="Professional tools"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#2D3748]" data-testid="arbeitsweise-cta">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-4">
            {t('workProcess.cta.title')}
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            {t('workProcess.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white text-lg h-14 px-8"
              data-testid="arbeitsweise-email-button"
            >
              <a href="mailto:info@mtrenovierung.de?subject=Anfrage%20für%20Renovierungsarbeiten">
                <Mail className="mr-2 h-5 w-5" />
                {t('common.sendEmail')}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#2D3748] text-lg h-14 px-8 bg-transparent"
              data-testid="arbeitsweise-phone-button"
            >
              <a href="tel:+4915207684235">
                <Phone className="mr-2 h-5 w-5" />
                +49 152 07684235
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
